import { formatDateTime, formatPrice } from "@/lib/hooks/utils";
import { ProductListResponseDto } from "@/lib/interfaces/product";
import { DataTableProps } from "@/lib/interfaces/utils";
import { Button } from "@/ui-components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui-components/ui/table";
import { ColumnDef, flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";
import { Package } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

const ProductsDataTable = ({
  data,
  isLoading,
  pageCount,
  pagination,
  setPagination,
}: DataTableProps<ProductListResponseDto>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const columns: ColumnDef<ProductListResponseDto>[] = [
    {
      accessorKey: "productId",
      header: ({ column }) => (
        <div className="flex items-center">
          <p> Produkt ID</p>
        </div>
      ),
      cell: ({ row }) => <div className="flex items-center">{row.original.productId}</div>,
    },
    {
      accessorKey: "title",
      header: ({ column }) => (
        <div className="flex items-center">
          <p>Titel</p>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4" />
          <span className="font-medium">{row.original.title}</span>
        </div>
      ),
    },
    {
      accessorFn: (row) => ({
        netPrice: row.netPrice,
        grossPrice: row.grossPrice,
      }),
      id: "price",
      header: ({ column }) => (
        <div className="flex items-center">
          <p>Materialkosten</p>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex flex-col py-0.5">
          <span className="font-medium">{formatPrice(row.original.netPrice)}</span>
          <span className="text-sm text-muted-foreground italic">{formatPrice(row.original.grossPrice)}</span>
        </div>
      ),
    },
    {
      accessorFn: (row) => ({
        netInstallationCost: row.netInstallationCost,
        grossInstallationCost: row.grossInstallationCost,
      }),
      id: "installationCost",
      header: ({ column }) => (
        <div className="flex items-center">
          <p>Montagekosten</p>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex flex-col py-0.5">
          <span className="font-medium">{formatPrice(row.original.netInstallationCost)}</span>
          <span className="text-sm text-muted-foreground italic">
            {formatPrice(row.original.grossInstallationCost)}
          </span>
        </div>
      ),
    },
    {
      accessorFn: (row) => ({
        totalNetPrice: row.totalNetPrice,
        totalGrossPrice: row.totalGrossPrice,
      }),
      id: "totalPrice",
      header: ({ column }) => (
        <div className="flex items-center">
          <p>Gesamt</p>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex flex-col py-0.5">
          <span className="font-medium">{formatPrice(row.original.totalNetPrice)}</span>
          <span className="text-sm text-muted-foreground italic">{formatPrice(row.original.totalGrossPrice)}</span>
        </div>
      ),
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <div className="flex items-center">
          <p>Erstellt am</p>
        </div>
      ),
      cell: ({ row }) => <div className="flex items-center">{formatDateTime(row.original.createdAt)}</div>,
    },
  ];

  const table = useReactTable({
    data,
    columns,
    pageCount,
    manualPagination: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 20 } },
    state: {
      pagination,
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={`cursor-pointer hover:bg-gray-100 ${row.original.isArchive ? "bg-gray-200" : ""}`}
                  onClick={() => navigate(`/settings/products/${row.original._id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Keine Produkte gefunden
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between space-x-2 py-4">
        <Button
          className="text-[17px] text-[#888] hover:text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.previousPage();
            const params = new URLSearchParams(searchParams);
            params.set("page", table.getState().pagination.pageIndex.toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanPreviousPage()}
        >
          Letzte
        </Button>
        <span className="text-sm text-[#D0D5DD]">
          Seite {table.getState().pagination.pageIndex + 1} von {table.getPageCount()}
        </span>
        <Button
          className="text-[17px] text-[#888] hover:text-[#12282A]"
          variant="outline"
          size="sm"
          onClick={() => {
            table.nextPage();
            const params = new URLSearchParams(searchParams);
            params.set("page", (table.getState().pagination.pageIndex + 2).toString());
            setSearchParams(params);
          }}
          disabled={!table.getCanNextPage()}
        >
          Nächste
        </Button>
      </div>
    </div>
  );
};

export default ProductsDataTable;
